import { isOutermostDropTarget } from '@components-library/tb-drag-and-drop/drag.utils'
import { CdkVirtualScrollViewport, ScrollingModule } from '@nomad2102npm/cdk/scrolling'
import { NgTemplateOutlet } from '@angular/common'
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    inject,
    Injector,
    Input,
    input,
    NgZone,
    signal,
    TemplateRef,
    output,
    viewChild,
    linkedSignal,
} from '@angular/core'
import { TbDragDropEvent } from '@components-library/tb-drag-and-drop/drag-drop.types'
import { TbDragAutoScrollDirective } from '@components-library/tb-drag-and-drop/tb-drag-auto-scroll.directive'
import { TbDropContainerDirective } from '@components-library/tb-drag-and-drop/tb-drop-container.directive'
import {
    findInColumn,
    findItemInColumns,
    isBoardCardData,
} from '@components-library/tb-board/board-utils'
import { TbColumn, TbDropEvent, TbMovedItem } from '@components-library/tb-board/board.model'
import {
    CollapseCallType,
    TbBoardColumnHeaderComponent,
} from '../tb-board-column-header/tb-board-column-header.component'

@Component({
    selector: 'app-tb-board-column',
    templateUrl: './tb-board-column.component.html',
    styleUrls: ['./tb-board-column.component.sass'],
    imports: [
        TbBoardColumnHeaderComponent,
        NgTemplateOutlet,
        ScrollingModule,
        TbDropContainerDirective,
        TbDragAutoScrollDirective,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
// TODO: make a interface BoardColumn { item: any, key: string }, instead of extends { guid: string } #2880
export class TbBoardColumnComponent<Item extends { guid: string }> {
    private readonly cd = inject(ChangeDetectorRef)
    private readonly ngZone = inject(NgZone)
    readonly componentInjector = inject(Injector)

    @Input({ required: true })
    items!: Item[]

    readonly card = input.required<TemplateRef<any>>()
    readonly columnItem = input.required<TbColumn<Item>>()
    readonly columns = input.required<TbColumn<Item>[]>()
    readonly headerMenu = input.required<TemplateRef<any>>()

    readonly disableDragIntoList = input<boolean>(false)
    readonly selectedItems = input<Item[]>([])

    readonly isCollapsedSet = input(false)
    readonly columnWidths = input<number[]>([])
    readonly collapsedInput = input(false, { alias: 'collapsed' })
    readonly collapsed = linkedSignal(() => this.collapsedInput())

    readonly dropItem = output<TbMovedItem<Item>>()
    readonly selectColumn = output<boolean>()
    readonly collapseChange = output<boolean>()

    readonly virtualScroll = viewChild.required(CdkVirtualScrollViewport)

    readonly cardInjector = computed(() => {
        const viewport = this.virtualScroll()
        return Injector.create({
            providers: [
                {
                    provide: CdkVirtualScrollViewport,
                    useValue: viewport,
                },
            ],
            parent: this.componentInjector,
        })
    })

    dragOn = signal(false)
    showPlaceholder = signal(false)

    toggleCollapse(collapseState: CollapseCallType) {
        this.collapsed.set(collapseState.check)

        if (collapseState.click) {
            this.collapseChange.emit(this.collapsed())
        }
    }

    trackBy(index: number, item: Item) {
        return item.guid
    }

    onDrag(args: TbDragDropEvent) {
        this.dragOn.set(true)

        const cursorOnTheColumnOnly = isOutermostDropTarget(args)
        const showPlaceholder = this.dragOn() && (this.items.length === 0 || cursorOnTheColumnOnly)
        this.showPlaceholder.set(showPlaceholder)
    }

    onDragLeave() {
        this.dragOn.set(false)
        this.showPlaceholder.set(false)
    }

    onDrop(args: TbDragDropEvent) {
        const draggedData = args.source.data
        if (!isBoardCardData<Item>(draggedData)) return

        const sourceItem = draggedData.boardCardData as Item
        const prevColumn = findItemInColumns(sourceItem.guid, this.columns())
        if (!prevColumn) return

        const previousIndex = findInColumn<Item>(prevColumn, sourceItem)

        this.showPlaceholder.set(false)
        this.dropCard({
            fromList: prevColumn.data,
            toList: this.columnItem().data,
            prevIndex: previousIndex,
            index: this.columnItem().data.length,
            data: sourceItem,
        })
    }

    dropCard(event: TbDropEvent<Item>) {
        this.dropItem.emit({
            item: event.data,
            previousItems: event.fromList,
            items: event.toList,
            previousIndex: event.prevIndex,
            currentIndex: event.index,
        })

        this.collapsed.set(false)
        this.ngZone.runOutsideAngular(() =>
            Promise.resolve().then(() => {
                // when the column become uncollapsed VirtualScroll should measure its sizes
                this.virtualScroll().checkViewportSize()
            }),
        )

        if (this.isCollapsedSet()) this.collapseChange.emit(this.collapsed())
        this.dragOn.set(false)

        this.cd.detectChanges()
    }
}
